import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Loader2 } from "lucide-react";

const BARS = 128;

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

let sharedCtx = null;
const getCtx = () => {
  if (!sharedCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    sharedCtx = new AC();
  }
  return sharedCtx;
};

// Decode an audio file into `BARS` normalized peak values (0..1)
async function computePeaks(src) {
  const res = await fetch(src);
  const buf = await res.arrayBuffer();
  const audioBuf = await getCtx().decodeAudioData(buf);
  const data = audioBuf.getChannelData(0);
  const block = Math.floor(data.length / BARS);
  const peaks = new Array(BARS).fill(0);
  let max = 0;
  for (let i = 0; i < BARS; i++) {
    let sum = 0;
    const start = i * block;
    for (let j = 0; j < block; j += 64) {
      const v = data[start + j] || 0;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / (block / 64 || 1));
    peaks[i] = rms;
    if (rms > max) max = rms;
  }
  return peaks.map((p) => (max ? p / max : 0));
}

export const WaveformPlayer = ({ track, index, isActive, onActivate, onEndedInactive }) => {
  const audioRef = useRef(null);
  const trackRef = useRef(null);
  const [peaks, setPeaks] = useState(null);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);

  // Load peaks once
  useEffect(() => {
    let alive = true;
    computePeaks(track.src)
      .then((p) => { if (alive) setPeaks(p); })
      .catch(() => { if (alive) setPeaks(new Array(BARS).fill(0).map(() => 0.15 + Math.random() * 0.7)); });
    return () => { alive = false; };
  }, [track.src]);

  // Pause when another track becomes active
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (!isActive && !a.paused) a.pause();
    if (isActive && a.paused) { a.play().catch(() => {}); }
  }, [isActive]);

  const progress = duration ? current / duration : 0;

  const seekToClientX = useCallback((clientX) => {
    const el = trackRef.current;
    const a = audioRef.current;
    if (!el || !a || !duration) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
    setCurrent(a.currentTime);
  }, [duration]);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => seekToClientX(e.touches ? e.touches[0].clientX : e.clientX);
    const up = () => setDragging(false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [dragging, seekToClientX]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (isActive && !a.paused) {
      a.pause();
      onActivate(null);
    } else {
      onActivate(track.id);
      a.play().catch(() => {});
    }
  };

  const playing = isActive && audioRef.current && !audioRef.current.paused;

  const bars = useMemo(() => peaks || new Array(BARS).fill(0.2), [peaks]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8%" }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1], delay: index * 0.06 }}
      className={`group border-b border-white/10 py-7 transition-colors duration-300 ${isActive ? "bg-white/[0.03]" : "hover:bg-white/[0.015]"}`}
    >
      <audio
        ref={audioRef}
        src={track.src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => !dragging && setCurrent(e.currentTarget.currentTime)}
        onEnded={() => { setCurrent(0); onActivate(null); onEndedInactive && onEndedInactive(); }}
      />

      <div className="flex items-center gap-5 sm:gap-8">
        <button
          data-testid={`track-play-${track.id}`}
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Play"}
          className={`shrink-0 flex h-14 w-14 items-center justify-center rounded-full border transition-all duration-300 ${isActive ? "bg-magenta border-magenta text-white glow-magenta" : "border-white/20 text-white/80 group-hover:border-magenta group-hover:text-magenta"}`}
        >
          {peaks === null ? <Loader2 className="h-5 w-5 animate-spin" /> : playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-display text-2xl sm:text-4xl tracking-[0.08em] text-white truncate">{track.title}</h3>
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-gold">{track.genre}</span>
            </div>
            <span data-testid={`track-time-${track.id}`} className="font-body text-xs sm:text-sm text-white/50 tabular-nums shrink-0">
              {fmt(current)} <span className="text-white/25">/ {duration ? fmt(duration) : track.len}</span>
            </span>
          </div>

          {/* Waveform */}
          <div
            ref={trackRef}
            data-testid={`track-waveform-${track.id}`}
            onPointerDown={(e) => { setDragging(true); onActivate(track.id); if (audioRef.current) audioRef.current.play().catch(() => {}); seekToClientX(e.clientX); }}
            className="mt-4 flex h-14 sm:h-16 items-center gap-[2px] cursor-pointer select-none touch-none"
          >
            {bars.map((h, i) => {
              const played = i / BARS <= progress;
              const height = 12 + h * 88; // percentage of container height
              return (
                <span
                  key={i}
                  className="flex-1 rounded-full transition-[background-color,opacity] duration-150"
                  style={{
                    height: `${height}%`,
                    minWidth: 1,
                    background: played
                      ? `linear-gradient(to top, #7A00FF, #FF007F)`
                      : "rgba(255,255,255,0.14)",
                    opacity: played ? 1 : 0.9,
                    boxShadow: played && playing ? "0 0 6px rgba(255,0,127,0.55)" : "none",
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
